import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import {
    FuseNavigationService,
    FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { QuickChatComponent } from 'app/layout/common/quick-chat/quick-chat.component';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { ShortcutsComponent } from 'app/layout/common/shortcuts/shortcuts.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { Subject, takeUntil } from 'rxjs';
import { TimelineActivitiesComponent } from 'app/layout/common/timeline-activities/timeline-activities.component';
import { UpdatesRequiredComponent } from 'app/layout/common/updates-required/updates-required.component';
import { MatSidenavModule } from '@angular/material/sidenav';
import { QuickAddUserComponent } from 'app/layout/common/quick-add-user/quick-add-user.component';
import { AddUserDrawerHostService } from 'app/layout/common/quick-add-user/quick-add-user-host.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MessagesComponent } from 'app/layout/common/messages/messages.component';

@Component({
    selector: 'thin-layout',
    templateUrl: './thin.component.html',
    encapsulation: ViewEncapsulation.None,
    imports: [
        FuseLoadingBarComponent,
        FuseVerticalNavigationComponent,
        MatButtonModule,
        MatIconModule,
        /* LanguagesComponent, */
        FuseFullscreenComponent,
        /* SearchComponent,
        ShortcutsComponent, */
       /*  NotificationsComponent, */
        UserComponent,
        RouterOutlet,
        QuickChatComponent,
        MatSidenavModule,
        MatTooltipModule,   
        MessagesComponent,
        QuickAddUserComponent,
        TimelineActivitiesComponent,
        UpdatesRequiredComponent,
    ],
})
export class ThinLayoutComponent implements OnInit, OnDestroy, AfterViewInit {
    isScreenSmall: boolean;
    navigation: Navigation;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    @ViewChild('addUserDrawer') addUserDrawer: any;

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _addUserHostService: AddUserDrawerHostService,
    ) {}

    /**
     * Public handler for the topbar "+ user" button.  Dispatches the
     * `open-add-user-drawer` window event so workspace inline hosts
     * registered with {@link AddUserDrawerHostService} can intercept
     * the request before the sliding drawer opens.
     */
    openAddUserDrawer(): void {
        try {
            window.dispatchEvent(new CustomEvent('open-add-user-drawer'));
        } catch {
            try { this.addUserDrawer?.open?.(); } catch {}
        }
    }

    ngAfterViewInit(): void {
        try {
            window.addEventListener('open-add-user-drawer', () => {
                try {
                    if (this._addUserHostService?.hasHost) {
                        console.log('[AddUser][Layout:Thin] inline host active; skipping sliding drawer');
                        return;
                    }
                    console.log('[AddUser][Layout:Thin] open');
                    this.addUserDrawer?.open?.();
                } catch (e) { console.error('[AddUser][Layout:Thin] fail', e); }
            });
        } catch {}
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });

        try {
            window.addEventListener('open-main-navigation', () => {
                try { this.toggleNavigation('mainNavigation'); } catch {}
            });
        } catch {}
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                name
            );

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }
}
